/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  EventEmitter,
  OnDestroy,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {UntypedFormArray, UntypedFormGroup} from '@angular/forms';
import {Subject} from 'rxjs';
import {map} from 'rxjs/operators';

import {Employee} from '../../../../shared/employees/employees.model';
import {isPresent} from '../../../utils/lang';
import {FormCardComponent} from '../../form-card/form-card.component';
import {FormValues, GroupZoneDescriptor, GroupZoneType} from '../../form-generator.interfaces';
import {FormGeneratorService} from '../../form-generator.service';
import {FormTableComponent} from '../../form-table/form-table.component';
import {FormTimelineComponent} from '../../form-timeline/form-timeline.component';

export interface GroupZoneControlGroup {
  zoneGroup: GroupZoneDescriptor;
  controlGroup: UntypedFormGroup | UntypedFormArray;
}

/**
 * Tab row component for the form generator.
 * A row/line of groupZones that can be card or table component depending on cardType
 */
@Component({
  selector: 'gp-form-tab-row',
  template: `
    <div class="row flex">
      <ng-template #cardsContainer></ng-template>
      <!-- Cards injected here -->
    </div>
  `,
  styleUrls: ['./form-tab-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormTabRowComponent implements AfterViewInit, OnDestroy {

  public zones: Array<GroupZoneDescriptor>;
  public locale: string;
  public currency: string;
  public cliRegID: number = null;

  public group: UntypedFormGroup;
  public data: FormValues;
  public loaded: EventEmitter<Array<GroupZoneControlGroup>> = new EventEmitter<Array<GroupZoneControlGroup>>();
  public readOnly: boolean = false;

  public cards: any = [];
  public groupedCards: any;
  public form: UntypedFormGroup;
  public tabSheetID: string;
  public activeCardSubject: Subject<boolean>;
  public isOnBoarding: boolean = false;
  public employee: Employee = null;
  public selectedOrg = null;

  @ViewChild('cardsContainer', {read: ViewContainerRef, static: true})
  private cardsContainer: ViewContainerRef;

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private cd: ChangeDetectorRef,
    private formGeneratorService: FormGeneratorService) {
  }

  public ngAfterViewInit(): any {
    this.loadCards().then(groupZonesControlGroup => {
      this.loaded.emit(groupZonesControlGroup);
      this.loaded.complete();
    });

    this.cd.detectChanges();
  }


  public ngOnDestroy() {
    this.cards.forEach((rowRef: ComponentRef<any>) => {
      rowRef.destroy();
      rowRef = null;
    });
    this.cards = [];
  }

  /**
   * Loads the passed cards inside this tab.
   *
   * @returns {Promise<Array<ControlGroup>>} a promise resolving to the array of loaded control groups from the form
   * cards when successful, or rejected otherwise.
   */
  private loadCards(): Promise<Array<GroupZoneControlGroup>> {

    this.ngOnDestroy();

    return Promise.all((this.zones).map((groupZoneDescriptor: GroupZoneDescriptor) => {
      const cardType: GroupZoneType = groupZoneDescriptor.groupZoneType;
      let component: any;

      if (groupZoneDescriptor.displayType === 'TIMELINE') {
        component = FormTimelineComponent;
      } else if (cardType === GroupZoneType.MULTIPLE_NON_MANDATORY || cardType === GroupZoneType.MULTIPLE_MANDATORY) {
        component = FormTableComponent;
      } else if ((cardType === GroupZoneType.UNIQUE_NON_MANDATORY || cardType === GroupZoneType.UNIQUE_MANDATORY)
        && groupZoneDescriptor.groupZoneKeyName) {
        component = FormTableComponent;
      } else {
        component = FormCardComponent;
      }

      const cardRef = this.cardsContainer.createComponent(
        this.componentFactoryResolver.resolveComponentFactory(component));
      this.cards.push(cardRef);

      this.cd.markForCheck();
      const card: any = cardRef.instance;

      // Prepare zone values for card. Performs a couple of sanity checks and logs warning in console when data
      // received is absent or doesn't match "dated" configuration of the zone group.
      let zoneValues = this.data.get(groupZoneDescriptor.groupZoneID);
      if (!isPresent(zoneValues) || zoneValues.length === 0) {
        zoneValues = this.formGeneratorService.createEmptyData(groupZoneDescriptor);
        this.data.set(groupZoneDescriptor.groupZoneID, zoneValues);
      }

      card.data = zoneValues;
      card.allData = this.data;
      // card._data = zoneValues;
      card.descriptor = groupZoneDescriptor;
      card.readOnly = this.readOnly || groupZoneDescriptor.readOnly;
      card.currency = this.currency;
      card.locale = this.locale;
      card.cliRegID = this.cliRegID;
      card.groupedCards = this.groupedCards;
      card.form = this.form;
      card.tabSheetID = this.tabSheetID;
      card.isOnBoarding = this.isOnBoarding;
      card.employee = this.employee;
      card.activeCardSubject = this.activeCardSubject;
      card.selectedOrg = this.selectedOrg;
      this.cd.markForCheck();
      card.init();

      return card.loaded.asObservable()
        .pipe(
          map(group => <GroupZoneControlGroup>{
            zoneGroup: groupZoneDescriptor,
            controlGroup: group
          })
        ).toPromise();
    }));
  }
}
